import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Message received. Nayan reads everything — slowly, but surely.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#121110] text-[#EFEAE1] py-28 lg:py-36" data-testid="contact-section">
      <div className="px-5 lg:px-10">
        <Reveal>
          <h2 className="font-giant text-[13vw] lg:text-[8vw] leading-[0.9]">
            LET'S T<span className="font-swash text-[#A8512E] text-[11vw] lg:text-[6.5vw]">a</span>LK
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-20">
          <div>
            <Reveal delay={0.1}>
              <p className="text-lg lg:text-xl text-[#EFEAE1]/70 leading-relaxed max-w-md">
                A venture idea, a partnership, or just curiosity about how the quiet way works —
                I'd rather have one meaningful conversation than a hundred shallow ones.
              </p>
              <a
                href="mailto:hello@nayanverma.com"
                className="link-ink inline-block font-swash text-3xl sm:text-4xl lg:text-5xl text-[#A8512E] mt-10 break-all"
                data-testid="contact-email-link"
              >
                hello@nayanverma.com
              </a>
            </Reveal>
            <Reveal delay={0.2} className="mt-12">
              <a
                href="https://wa.me/917042874553?text=Hi%20Nayan%2C%20I%27d%20like%20to%20book%20a%20call%20with%20you."
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-4 rounded-2xl bg-[#25D366] px-7 py-5 transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
                data-testid="book-call-btn"
              >
                <span className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <svg viewBox="0 0 24 24" width="26" height="26" fill="#FFFFFF" aria-hidden="true">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </span>
                <span className="text-left">
                  <span className="block text-white/80 text-xs uppercase tracking-[0.2em] font-medium">Direct chat, no forms</span>
                  <span className="block text-white font-semibold text-xl mt-1">Book a call on WhatsApp</span>
                </span>
                <ArrowUpRight size={20} className="text-white/80 ml-2 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <p className="text-sm text-[#EFEAE1]/40 mt-4">Opens WhatsApp directly — message or call, whatever feels comfortable.</p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-sm text-[#EFEAE1]/40 mt-12 leading-relaxed max-w-xs">
                I-Thum Tower-A, Unit 207, Sector 62,<br />Noida — 201309, India
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input className="input-ink" placeholder="Your name" value={form.name} onChange={update("name")} data-testid="contact-name-input" />
                <input className="input-ink" type="email" placeholder="Your email" value={form.email} onChange={update("email")} data-testid="contact-email-input" />
              </div>
              <input className="input-ink" placeholder="Subject (optional)" value={form.subject} onChange={update("subject")} data-testid="contact-subject-input" />
              <textarea
                className="input-ink resize-none"
                rows={5}
                placeholder="Tell me about your idea. Take your time — I certainly will."
                value={form.message}
                onChange={update("message")}
                data-testid="contact-message-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="pill pill--light text-base px-10 py-4 disabled:opacity-50"
                style={{ background: "#A8512E", borderColor: "#A8512E", color: "#EFEAE1" }}
                data-testid="contact-submit-button"
              >
                {sending ? "Sending…" : "Send the message"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
